import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { createFetch, createSchema } from '@better-fetch/fetch';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// types
import {
  ApiError,
  ApiErrorSchema,
  DataItemEnabledCondition,
  DataItemType,
  FileBrowserPage,
  JobErrorType,
  JobType,
  NotificationType,
  Site,
  SiteStorageArea,
} from '../../utils/types';
import { InitiateDMSearchParams } from '../../utils/events';

// functions
import { useTranslation } from 'react-i18next';
import { APIPrefix } from '../../utils/functions';
import { initiateDMSearch } from './file-browser-actions';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

export enum RenameType {
  NONE,
  EXISTING,
  NEW,
} // RenameType

//  ------------------------------------------------------------
//
//  hold the parameters used to initiate the current search.
//
//  ------------------------------------------------------------

interface PageParams {
  //
  pageDisplayed: FileBrowserPage;

  filenameFilter: string[];
  fileTypeFilter: string;

  showHiddenFiles: boolean;

  namespace: string | undefined;
  collection:
    | {
        id: number;
        name: string;
      }
    | undefined;
  jobID: string | undefined;
  dataset: string | undefined;

  storagePath: string[];
  //
} // PageParams

export interface FileBrowserState {
  //
  loadingComponent: boolean;
  latestQueryID: number;

  pageParams: PageParams;
  previousParams: PageParams;

  dataList: DataItemType[];
  lifetimeStr: string;
  lifetime: number;
  dataManagementJobs: JobType[];
  jobsLoading: boolean;
  jobUpdates: NotificationType[];
  selectedItems: DataItemType[];
  selectedDivs: number[];
  errorMessage: string;
  vosUri: string;
  parametersMaximised: boolean;
  detailsMaximised: boolean;
  computeMaximised: boolean;
  displayComputePanel: boolean;
  renameIndex: number;
  renameType: RenameType;
  renameFilename: string;
  openFileDialogTrigger: number;
  contextMenuRefresh: number;

  // store the items in the clipboard, and a flag to say whether or not we are
  // currently in the same folder where these items are stored.
  clipboard: string[];
  clipboardPath: string[];
  inClipboardFolder: boolean;
  focusElement: { divID: number };
  //
} // FileBrowserState

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

const initialState: FileBrowserState = {
  loadingComponent: false,
  latestQueryID: -1,
  pageParams: {
    pageDisplayed: FileBrowserPage.FileBrowser,
    filenameFilter: [''],
    fileTypeFilter: 'all',
    showHiddenFiles: false,
    namespace: undefined,
    jobID: undefined,
    dataset: undefined,
    collection: undefined,
    storagePath: [],
  },
  previousParams: {
    pageDisplayed: FileBrowserPage.FileBrowser,
    filenameFilter: [''],
    fileTypeFilter: 'all',
    showHiddenFiles: false,
    namespace: undefined,
    jobID: undefined,
    dataset: undefined,
    collection: undefined,
    storagePath: [],
  },
  dataList: [],
  lifetimeStr: '3600',
  lifetime: 3600,
  dataManagementJobs: [],
  jobsLoading: false,
  jobUpdates: [],
  selectedItems: [],
  selectedDivs: [],
  errorMessage: '',
  vosUri: '',
  parametersMaximised: true,
  detailsMaximised: true,
  computeMaximised: true,
  displayComputePanel: false,
  renameIndex: -1,
  renameType: RenameType.NONE,
  renameFilename: '',
  clipboard: [],
  clipboardPath: [],
  inClipboardFolder: false,
  openFileDialogTrigger: 0,
  contextMenuRefresh: 0,
  focusElement: { divID: 0 },
};

//	--------------------------------------------------------------------------
//
//	F U N C T I O N S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	S L I C E S
//
//	--------------------------------------------------------------------------

export const fileBrowserSlice = createSlice({
  name: 'fileBrowser',
  initialState: initialState,
  reducers: {
    //
    addToSelectedDivs: (state: FileBrowserState, action: PayloadAction<number[]>) => {
      state.selectedDivs = [
        ...state.selectedDivs,
        ...action.payload.filter((filter) => {
          return state.selectedDivs.indexOf(filter) === -1;
        }),
      ];
    },

    removeFromSelectedDivs: (
      state: FileBrowserState,
      action: PayloadAction<number[]>,
    ) => {
      state.selectedDivs = state.selectedDivs.filter((filter: number) => {
        return action.payload.indexOf(filter) === -1;
      });
    },

    restorePageParams: (state: FileBrowserState) => {
      //
      state.pageParams = Object.assign({}, state.previousParams);
      state.pageParams.filenameFilter = Object.assign(
        [],
        state.previousParams.filenameFilter,
      );
      state.pageParams.storagePath = Object.assign([], state.previousParams.storagePath);
      //
    },

    savePageParams: (state: FileBrowserState) => {
      //
      state.previousParams = Object.assign({}, state.pageParams);
      state.previousParams.filenameFilter = Object.assign(
        [],
        state.pageParams.filenameFilter,
      );
      state.previousParams.storagePath = Object.assign([], state.pageParams.storagePath);
      //
    },

    setFocusElement: (
      state: FileBrowserState,
      action: PayloadAction<{ divID: number }>,
    ) => {
      state.focusElement = action.payload;
    },

    setLatestQueryID: (state: FileBrowserState, action: PayloadAction<number>) => {
      state.latestQueryID = action.payload;
    },

    setLoadingComponent: (state: FileBrowserState, action: PayloadAction<boolean>) => {
      state.loadingComponent = action.payload;
    },

    setPageDisplayed: (
      state: FileBrowserState,
      action: PayloadAction<FileBrowserPage>,
    ) => {
      state.pageParams.pageDisplayed = action.payload;
    },

    setDataList: (state: FileBrowserState, action: PayloadAction<DataItemType[]>) => {
      state.dataList = action.payload;
    },

    setFilenameFilter: (state: FileBrowserState, action: PayloadAction<string[]>) => {
      state.pageParams.filenameFilter = action.payload;
    },

    setFileTypeFilter: (state: FileBrowserState, action: PayloadAction<string>) => {
      state.pageParams.fileTypeFilter = action.payload;
    },

    setLifetimeStr: (state: FileBrowserState, action: PayloadAction<string>) => {
      state.lifetimeStr = action.payload;
    },

    setLifetime: (state: FileBrowserState, action: PayloadAction<number>) => {
      state.lifetime = action.payload;
    },

    setShowHiddenFiles: (state: FileBrowserState, action: PayloadAction<boolean>) => {
      state.pageParams.showHiddenFiles = action.payload;
    },

    setNamespace: (
      state: FileBrowserState,
      action: PayloadAction<string | undefined>,
    ) => {
      state.pageParams.namespace = action.payload;
    },

    setDataManagementJobs: (
      state: FileBrowserState,
      action: PayloadAction<JobType[]>,
    ) => {
      state.dataManagementJobs = action.payload;
    },

    updateDataManagementJobs: (
      state: FileBrowserState,
      action: PayloadAction<NotificationType[]>,
    ) => {
      for (const notification of action.payload) {
        for (const job of state.dataManagementJobs) {
          if (
            notification.additionalInfo.data_management_job_id === job.job_id &&
            notification.additionalInfo.updated_at > job.updated_at
          ) {
            job.status = notification.sseExtras.status;
            job.updated_at = notification.additionalInfo.updated_at;
          }
          if (
            notification.sseExtras.error_message &&
            notification.sseExtras.error_timestamp
          ) {
            let jobError: JobErrorType = {
              message: notification.sseExtras.error_message,
              created_at: notification.sseExtras.error_timestamp,
            };
            job.error_message = jobError;
          }
        }
      }

      state.jobUpdates = [];
    },

    setJobID: (state: FileBrowserState, action: PayloadAction<string | undefined>) => {
      state.pageParams.jobID = action.payload;
    },

    setJobsLoading: (state: FileBrowserState, action: PayloadAction<boolean>) => {
      state.jobsLoading = action.payload;
    },

    appendJobUpdates: (
      state: FileBrowserState,
      action: PayloadAction<NotificationType>,
    ) => {
      for (const update of state.jobUpdates) {
        if (update.notificationID === action.payload.notificationID) return;
      }
      state.jobUpdates.push(action.payload);
    },

    setDataset: (state: FileBrowserState, action: PayloadAction<string | undefined>) => {
      state.pageParams.dataset = action.payload;
    },

    setCollection: (
      state: FileBrowserState,
      action: PayloadAction<{ id: number; name: string } | undefined>,
    ) => {
      state.pageParams.collection = action.payload;
    },

    setStoragePath: (state: FileBrowserState, action: PayloadAction<string[]>) => {
      state.pageParams.storagePath = action.payload;
    },

    setSelectedItems: (
      state: FileBrowserState,
      action: PayloadAction<DataItemType[]>,
    ) => {
      state.selectedItems = action.payload;
    },

    setSelectedDivs: (state: FileBrowserState, action: PayloadAction<number[]>) => {
      state.selectedDivs = action.payload;
    },

    setErrorMessage: (state: FileBrowserState, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },

    setVosUri: (state: FileBrowserState, action: PayloadAction<string>) => {
      state.vosUri = action.payload;
    },

    setParametersMaximised: (state: FileBrowserState, action: PayloadAction<boolean>) => {
      state.parametersMaximised = action.payload;
    },

    setDetailsMaximised: (state: FileBrowserState, action: PayloadAction<boolean>) => {
      state.detailsMaximised = action.payload;
    },

    setComputeMaximised: (state: FileBrowserState, action: PayloadAction<boolean>) => {
      state.computeMaximised = action.payload;
    },

    setDisplayComputePanel: (state: FileBrowserState, action: PayloadAction<boolean>) => {
      state.displayComputePanel = action.payload;
    },

    setRenameIndex: (state: FileBrowserState, action: PayloadAction<number>) => {
      state.renameIndex = action.payload;
    },

    setRenameType: (state: FileBrowserState, action: PayloadAction<RenameType>) => {
      state.renameType = action.payload;
    },

    setRenameFilename: (state: FileBrowserState, action: PayloadAction<string>) => {
      state.renameFilename = action.payload;
    },

    setClipboard: (state: FileBrowserState, action: PayloadAction<string[]>) => {
      state.clipboard = action.payload;
    },

    setClipboardPath: (state: FileBrowserState, action: PayloadAction<string[]>) => {
      state.clipboardPath = action.payload;
    },

    setInClipboardFolder: (state: FileBrowserState, action: PayloadAction<boolean>) => {
      state.inClipboardFolder = action.payload;
    },

    setContextMenuRefresh: (state: FileBrowserState, action: PayloadAction<number>) => {
      state.contextMenuRefresh = action.payload;
    },

    setOpenFileDialogTrigger: (
      state: FileBrowserState,
      action: PayloadAction<number>,
    ) => {
      state.openFileDialogTrigger = action.payload;
    },
    //
  },
  //extraReducers: (builder) => {
  //  builder
  //    .addCase(initiateDMSearch.fulfilled, (state: FileBrowserState) => {})
  //},
});

// expose the interface.
export const {
  addToSelectedDivs,
  removeFromSelectedDivs,
  restorePageParams,
  savePageParams,
  setFocusElement,
  setLoadingComponent,
  setLatestQueryID,
  setPageDisplayed,
  setDataList,
  setFilenameFilter,
  setFileTypeFilter,
  setLifetimeStr,
  setLifetime,
  setShowHiddenFiles,
  setNamespace,
  setDataManagementJobs,
  updateDataManagementJobs,
  setJobID,
  setJobsLoading,
  appendJobUpdates,
  setDataset,
  setCollection,
  setStoragePath,
  setSelectedItems,
  setSelectedDivs,
  setErrorMessage,
  setVosUri,
  setParametersMaximised,
  setDetailsMaximised,
  setComputeMaximised,
  setDisplayComputePanel,
  setRenameIndex,
  setRenameType,
  setRenameFilename,
  setClipboard,
  setClipboardPath,
  setInClipboardFolder,
  setOpenFileDialogTrigger,
  setContextMenuRefresh,
} = fileBrowserSlice.actions;
export default fileBrowserSlice.reducer;
